import { useCallback, useEffect, useRef, useState } from "react";
import {
  ChimeVideoService,
  ConnectionState,
  MeetingEndInfo,
  RemoteParticipantState,
  CameraState,
} from "../services/chimeVideo";
import type {
  ChimeAttendeeInfo,
  ChimeMeetingInfo,
  ParticipantRole,
} from "../types/video";

interface InitializeOptions {
  participantRole: ParticipantRole;
  expiresAt: string;
  recordingStarted: boolean;
}

export const useChimeVideo = () => {
  const serviceRef = useRef<ChimeVideoService | null>(null);
  if (!serviceRef.current) {
    serviceRef.current = new ChimeVideoService();
  }

  const meetingEndHandlerRef = useRef<
    ((endInfo: MeetingEndInfo) => void) | null
  >(null);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("idle");
  const [hasConnected, setHasConnected] = useState(false);
  const [remoteParticipantState, setRemoteParticipantState] =
    useState<RemoteParticipantState>("waiting");
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isLocalVideoReady, setIsLocalVideoReady] = useState(false);
  const [requiresAudioActivation, setRequiresAudioActivation] = useState(false);
  const [requiresVideoActivation, setRequiresVideoActivation] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [meetingEnded, setMeetingEnded] = useState<MeetingEndInfo | null>(null);
  const [cameraState, setCameraState] = useState<CameraState>({ status: "off", mode: "replacement", error: null });

  const initialize = useCallback(
    async (
      meetingInfo: ChimeMeetingInfo,
      attendeeInfo: ChimeAttendeeInfo,
      options: InitializeOptions,
    ) => {
      const service = serviceRef.current!;
      setError(null);
      setWarning(null);
      setMeetingEnded(null);
      setHasConnected(false);
      setRemoteParticipantState("waiting");
      service.setHandlers({
        onConnectionStateChange: (state) => {
          setConnectionState(state);
          if (state === "connected") setHasConnected(true);
          if (state === "idle" || state === "ended" || state === "error") {
            setHasConnected(false);
          }
        },
        onRemoteParticipantStateChange: setRemoteParticipantState,
        onLocalVideoStateChange: setIsLocalVideoReady,
        onLocalVideoDesiredChange: setIsVideoEnabled,
        onCameraStateChange: setCameraState,
        onAudioActivationRequired: setRequiresAudioActivation,
        onVideoActivationRequired: setRequiresVideoActivation,
        onRecordingStateChange: setIsRecording,
        onWarning: setWarning,
        onMeetingEnded: (endInfo) => {
          setMeetingEnded(endInfo);
          meetingEndHandlerRef.current?.(endInfo);
        },
      });

      try {
        await service.initialize(meetingInfo, attendeeInfo, options);
      } catch (caughtError) {
        const message =
          caughtError instanceof Error ? caughtError.message : "視訊初始化失敗";
        setConnectionState("error");
        setError(message);
        throw caughtError;
      }
    },
    [],
  );

  const retryLocalVideo = useCallback(async () => {
    try {
      await serviceRef.current!.retryProtectedVideo();
    } catch {
      // The service reports camera failures without changing the user's intent.
    }
  }, []);

  const startMeeting = useCallback(async () => {
    const service = serviceRef.current!;
    try {
      setError(null);
      await service.startAudioVideo();
    } catch (caughtError) {
      const message =
        caughtError instanceof Error ? caughtError.message : "視訊連線失敗";
      setConnectionState("error");
      setError(message);
      throw caughtError;
    }

    // Recording and connected audio must not wait on a model download or camera permission.
    void service.startLocalVideo().catch(() => undefined);
  }, []);

  const stopMeeting = useCallback(async () => {
    setHasConnected(false);
    await serviceRef.current?.stop();
    setConnectionState("idle");
    setRemoteParticipantState("waiting");
    setIsMuted(false);
    setIsVideoEnabled(false);
    setIsLocalVideoReady(false);
    setCameraState({ status: "off", mode: "replacement", error: null });
    setRequiresAudioActivation(false);
    setRequiresVideoActivation(false);
    setIsRecording(false);
    setWarning(null);
  }, []);

  const toggleMute = useCallback(() => {
    const service = serviceRef.current!;
    if (isMuted) {
      service.unmuteAudio();
    } else {
      service.muteAudio();
    }
    setIsMuted((current) => !current);
  }, [isMuted]);

  const toggleVideo = useCallback(async () => {
    const service = serviceRef.current!;
    if (service.isLocalVideoRequested()) {
      await service.stopLocalVideo();
      return;
    }

    await service.startLocalVideo().catch(() => undefined);
  }, []);

  const enableRawVideo = useCallback(async () => {
    await serviceRef.current?.enableRawVideo().catch(() => undefined);
  }, []);

  const closeVideo = useCallback(async () => {
    await serviceRef.current?.stopLocalVideo();
  }, []);

  const switchCamera = useCallback(async () => {
    await serviceRef.current?.switchCamera().catch(() => undefined);
  }, []);

  const resumeAudioPlayback = useCallback(async () => {
    await serviceRef.current?.resumeAudioPlayback();
  }, []);

  const resumeVideoPlayback = useCallback(async () => {
    await serviceRef.current?.resumeVideoPlayback();
  }, []);

  const broadcastRecordingState = useCallback((recording: boolean) => {
    serviceRef.current?.broadcastRecordingState(recording);
  }, []);

  const setLocalVideoElement = useCallback(
    (element: HTMLVideoElement | null) => {
      serviceRef.current?.setLocalVideoElement(element);
    },
    [],
  );

  const setRemoteVideoElement = useCallback(
    (element: HTMLVideoElement | null) => {
      serviceRef.current?.setRemoteVideoElement(element);
    },
    [],
  );

  const setAudioElement = useCallback((element: HTMLAudioElement | null) => {
    serviceRef.current?.setAudioElement(element);
  }, []);

  const setOnMeetingEnded = useCallback(
    (handler: ((endInfo: MeetingEndInfo) => void) | null) => {
      meetingEndHandlerRef.current = handler;
    },
    [],
  );

  useEffect(() => {
    return () => {
      meetingEndHandlerRef.current = null;
      void serviceRef.current?.stop();
    };
  }, []);

  return {
    connectionState,
    isConnected: connectionState === "connected",
    isMeetingActive:
      hasConnected &&
      (connectionState === "connected" || connectionState === "reconnecting"),
    isConnecting:
      connectionState === "connecting" || connectionState === "reconnecting",
    remoteParticipantState,
    error,
    warning,
    isMuted,
    isVideoEnabled,
    isLocalVideoReady,
    requiresAudioActivation,
    requiresVideoActivation,
    isRecording,
    meetingEnded,
    cameraState,
    initialize,
    startMeeting,
    stopMeeting,
    toggleMute,
    toggleVideo,
    retryLocalVideo,
    enableRawVideo,
    closeVideo,
    switchCamera,
    resumeAudioPlayback,
    resumeVideoPlayback,
    broadcastRecordingState,
    setLocalVideoElement,
    setRemoteVideoElement,
    setAudioElement,
    setOnMeetingEnded,
  };
};
